import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    LogOut, Edit3, Settings, HeartHandshake, Sun, Moon,
    Bell, HelpCircle, Share2, Sparkles, X, Copy, Check,
    Mail, MessageSquare, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { usePaywall } from "@/contexts/PaywallContext";
import { photoUrl, api } from "@/lib/api";
import { toast } from "sonner";
import { enablePush, disablePush, pushSupported, pushPermission } from "@/lib/push";
import VerificationModal from "@/components/VerificationModal";
import VerifiedBadge from "@/components/VerifiedBadge";
import { ShieldCheck } from "lucide-react";

export default function MenuTab() {
    const { user, logout } = useAuth();
    const { theme, toggle: toggleTheme } = useTheme();
    const { status, openPaywall, refreshStatus } = usePaywall();
    const navigate = useNavigate();
    const avatar = user?.picture || (user?.photos?.[0] ? photoUrl(user.photos[0]) : null);

    const [notifPrefs, setNotifPrefs] = useState({
        matches: true,
        messages: true,
        marketing: false,
    });
    const [notifOpen, setNotifOpen] = useState(false);
    const [supportOpen, setSupportOpen] = useState(false);
    const [referralOpen, setReferralOpen] = useState(false);
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [vStatus, setVStatus] = useState(user?.verification_status || (user?.verified ? "approved" : null));

    useEffect(() => {
        setVStatus(user?.verification_status || (user?.verified ? "approved" : null));
    }, [user?.verification_status, user?.verified]);

    const isVerified = !!user?.verified;
    const verifyLabel = isVerified
        ? "Verified ✓"
        : vStatus === "pending"
            ? "Verification pending"
            : vStatus === "rejected"
                ? "Verification failed — retry"
                : "Get verified";

    useEffect(() => {
        refreshStatus?.();
    }, [refreshStatus]);

    useEffect(() => {
        const s = user?.settings || {};
        setNotifPrefs({
            matches: s.notifications_matches ?? true,
            messages: s.notifications_messages ?? true,
            marketing: s.notifications_marketing ?? false,
        });
    }, [user]);

    const handleLogout = async () => {
        await logout();
        navigate("/", { replace: true });
    };

    const isPremium = !!status?.premium;

    return (
        <div className="mx-auto max-w-xl px-5 pt-10 pb-24" data-testid="menu-tab">
            {/* Profile Card */}
            <div className="rounded-4xl border border-border bg-card p-6 flex items-center gap-4">
                <div className="h-20 w-20 rounded-3xl overflow-hidden bg-accent grid place-items-center text-accent-foreground text-3xl font-black">
                    {avatar ? (
                        <img src={avatar} alt="me" className="h-full w-full object-cover" data-testid="menu-avatar-img" />
                    ) : (
                        (user?.name || "?").charAt(0).toUpperCase()
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-display text-2xl font-black truncate flex items-center gap-1.5" data-testid="menu-name">
                        {user?.name}
                        <VerifiedBadge verified={isVerified} size="md" />
                    </div>
                    <div className="text-sm text-muted-foreground truncate" data-testid="menu-email">
                        {user?.email}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5">
                        <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                                isPremium
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-accent text-accent-foreground"
                            }`}
                            data-testid="menu-plan-badge"
                        >
                            <Sparkles className="h-3 w-3" strokeWidth={3} />
                            {isPremium ? "Premium" : "Free"}
                        </span>
                    </div>
                </div>
            </div>

            {/* Premium CTA */}
            {!isPremium && (
                <motion.button
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => openPaywall({ reason: "generic" })}
                    data-testid="menu-upgrade-btn"
                    className="mt-4 w-full text-left rounded-3xl bg-primary text-primary-foreground p-5 lift relative overflow-hidden"
                >
                    <div className="blob" style={{ width: 160, height: 160, top: -50, right: -30, background: "white", opacity: 0.15 }} />
                    <div className="relative flex items-center gap-4">
                        <div className="h-11 w-11 rounded-2xl bg-white/20 grid place-items-center">
                            <Sparkles className="h-5 w-5" strokeWidth={2.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display text-lg font-black">Go Premium</div>
                            <div className="text-xs opacity-90">Unlimited swipes, chats & listings for ₹{status?.price_inr ?? 99}/mo</div>
                        </div>
                        <ChevronRight className="h-5 w-5 opacity-80" strokeWidth={2.5} />
                    </div>
                </motion.button>
            )}

            {/* Usage stats */}
            {!isPremium && status && (
                <div className="mt-3 rounded-3xl border border-border bg-card p-4">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Today's usage</div>
                    <div className="space-y-2 text-sm">
                        <UsageBar
                            label="Swipes"
                            used={status.swipes_today || 0}
                            limit={status.swipe_limit || 10}
                            testId="usage-swipes"
                        />
                        <UsageBar
                            label="Chats unlocked"
                            used={status.unlocked_chats_used || 0}
                            limit={status.chat_limit || 1}
                            testId="usage-chats"
                        />
                        <UsageBar
                            label="Listings"
                            used={status.active_listings || 0}
                            limit={status.listing_limit || 1}
                            testId="usage-listings"
                        />
                    </div>
                </div>
            )}

            {/* Vibe tags */}
            {!!user?.vibe_tags?.length && (
                <div className="mt-4 rounded-3xl border border-border bg-card p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="font-display font-bold">Your vibe</div>
                        <button
                            onClick={() => navigate("/onboarding/vibe")}
                            data-testid="menu-retake-vibe-inline"
                            className="text-xs font-semibold text-primary hover:underline"
                        >
                            Retake
                        </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {user.vibe_tags.map((t) => (
                            <span key={t} className="vibe-pill" data-testid={`menu-tag-${t}`}>{t}</span>
                        ))}
                    </div>
                </div>
            )}

            {/* Section: Profile */}
            <SectionTitle>Profile</SectionTitle>
            <div className="space-y-2">
                <ActionRow
                    icon={ShieldCheck}
                    label={verifyLabel}
                    onClick={() => setVerifyOpen(true)}
                    testId="menu-verify"
                    rightSlot={isVerified ? (
                        <span className="text-[10px] font-black uppercase tracking-widest text-primary">verified</span>
                    ) : vStatus === "pending" ? (
                        <span className="text-[10px] font-black uppercase tracking-widest bg-amber-500/20 text-amber-600 rounded-full px-2 py-0.5">
                            in review
                        </span>
                    ) : vStatus === "rejected" ? (
                        <span className="text-[10px] font-black uppercase tracking-widest bg-destructive/15 text-destructive rounded-full px-2 py-0.5">
                            retry
                        </span>
                    ) : (
                        <span className="text-[10px] font-black uppercase tracking-widest bg-primary text-primary-foreground rounded-full px-2 py-0.5">
                            new
                        </span>
                    )}
                />
                <ActionRow icon={Edit3} label="Edit profile & photos" onClick={() => navigate("/onboarding/profile")} testId="menu-edit-profile" />
                <ActionRow icon={HeartHandshake} label="Retake Vibe Quiz" onClick={() => navigate("/onboarding/vibe")} testId="menu-retake-vibe" />
            </div>

            {/* Section: Preferences */}
            <SectionTitle>Preferences</SectionTitle>
            <div className="space-y-2">
                <div className="w-full flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-4">
                    <div className="h-9 w-9 rounded-xl bg-accent grid place-items-center text-accent-foreground">
                        {theme === "dark" ? <Moon className="h-4 w-4" strokeWidth={2.5} /> : <Sun className="h-4 w-4" strokeWidth={2.5} />}
                    </div>
                    <div className="font-semibold flex-1">Dark mode</div>
                    <Switch
                        checked={theme === "dark"}
                        onCheckedChange={toggleTheme}
                        data-testid="menu-theme-toggle"
                        aria-label="Toggle dark mode"
                    />
                </div>
                <ActionRow icon={Bell} label="Notifications" onClick={() => setNotifOpen(true)} testId="menu-notifications" />
            </div>

            {/* Section: Community */}
            <SectionTitle>Community</SectionTitle>
            <div className="space-y-2">
                <ActionRow icon={Share2} label="Invite a friend" onClick={() => setReferralOpen(true)} testId="menu-invite" />
                <ActionRow icon={HelpCircle} label="Help & support" onClick={() => setSupportOpen(true)} testId="menu-support" />
            </div>

            <Button
                variant="outline"
                onClick={handleLogout}
                data-testid="menu-logout-btn"
                className="mt-6 h-12 w-full rounded-2xl font-semibold border-destructive/30 text-destructive hover:bg-destructive/10 lift"
            >
                <LogOut className="mr-2 h-4 w-4" /> Log out
            </Button>

            <div className="mt-4 text-center text-[10px] text-muted-foreground">
                Flatch · v1.0 · made for the vibe-first generation
            </div>

            <NotifSheet
                open={notifOpen}
                onClose={() => setNotifOpen(false)}
                prefs={notifPrefs}
                onChange={async (next) => {
                    setNotifPrefs(next);
                    try {
                        await api.put("/settings", {
                            notifications_matches: next.matches,
                            notifications_messages: next.messages,
                            notifications_marketing: next.marketing,
                        });
                    } catch (e) {
                        toast.error("Couldn't save");
                    }
                }}
            />
            <SupportSheet open={supportOpen} onClose={() => setSupportOpen(false)} />
            <ReferralSheet open={referralOpen} onClose={() => setReferralOpen(false)} user={user} />
            <VerificationModal
                open={verifyOpen}
                onClose={() => setVerifyOpen(false)}
                onVerified={() => { setVStatus("pending"); refreshStatus?.(); }}
            />
        </div>
    );
}

function SectionTitle({ children }) {
    return <div className="mt-6 mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">{children}</div>;
}

function ActionRow({ icon: Icon, label, onClick, testId, rightSlot }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className="w-full flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-4 text-left hover:bg-accent/50 lift"
        >
            <div className="h-9 w-9 rounded-xl bg-accent grid place-items-center text-accent-foreground">
                <Icon className="h-4 w-4" strokeWidth={2.5} />
            </div>
            <div className="font-semibold flex-1">{label}</div>
            {rightSlot || <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={2.5} />}
        </button>
    );
}

function UsageBar({ label, used, limit, testId }) {
    const pct = Math.min(100, (used / Math.max(1, limit)) * 100);
    const full = used >= limit;
    return (
        <div data-testid={testId}>
            <div className="flex justify-between text-xs">
                <span className="font-semibold">{label}</span>
                <span className={full ? "text-destructive font-bold" : "text-muted-foreground"}>
                    {used}/{limit}
                </span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                    className={`h-full rounded-full ${full ? "bg-destructive" : "bg-primary"}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

function BottomSheet({ open, onClose, title, children, testId }) {
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    data-testid={testId}
                    className="fixed inset-0 z-[70] bg-background/70 backdrop-blur-xl"
                >
                    <motion.div
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{ type: "spring", stiffness: 260, damping: 30 }}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute inset-x-0 bottom-0 mx-auto max-w-xl rounded-t-4xl border-t border-border bg-card p-6 pb-8"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="font-display text-2xl font-black">{title}</div>
                            <button onClick={onClose} className="h-9 w-9 rounded-full hover:bg-accent grid place-items-center">
                                <X className="h-5 w-5" strokeWidth={2.5} />
                            </button>
                        </div>
                        {children}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function NotifSheet({ open, onClose, prefs, onChange }) {
    const rows = [
        { key: "matches", label: "New matches", desc: "When someone likes you back" },
        { key: "messages", label: "New messages", desc: "When a match sends you a message" },
        { key: "marketing", label: "Tips & offers", desc: "Rare, only the good stuff" },
    ];
    const [pushOn, setPushOn] = useState(false);
    const [pushBusy, setPushBusy] = useState(false);
    const supported = pushSupported();

    useEffect(() => {
        if (!open || !supported) return;
        (async () => {
            try {
                const reg = await navigator.serviceWorker.getRegistration();
                const sub = reg ? await reg.pushManager.getSubscription() : null;
                setPushOn(!!sub && pushPermission() === "granted");
            } catch (_) {
                setPushOn(false);
            }
        })();
    }, [open, supported]);

    const togglePush = async (v) => {
        if (pushBusy) return;
        setPushBusy(true);
        try {
            if (v) {
                const res = await enablePush();
                if (res === "granted") {
                    setPushOn(true);
                    toast.success("Push notifications on 🔔");
                } else if (res === "denied") {
                    toast.error("Notifications blocked. Enable them in browser settings.");
                } else if (res === "unsupported") {
                    toast.error("Push not supported in this browser");
                } else {
                    toast.error("Couldn't enable push");
                }
            } else {
                await disablePush();
                setPushOn(false);
                toast("Push notifications off");
            }
        } finally {
            setPushBusy(false);
        }
    };

    return (
        <BottomSheet open={open} onClose={onClose} title="Notifications" testId="notif-sheet">
            <div className="space-y-2">
                <div className="flex items-center gap-3 rounded-2xl border-2 border-primary bg-primary/5 px-4 py-3" data-testid="push-toggle-row">
                    <div className="flex-1">
                        <div className="font-semibold flex items-center gap-1.5">
                            Browser push {supported ? "" : <span className="text-xs text-muted-foreground">(unsupported)</span>}
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {supported ? "Get instant alerts even when Flatch is closed" : "Try on a supported browser"}
                        </div>
                    </div>
                    <Switch
                        checked={pushOn}
                        onCheckedChange={togglePush}
                        disabled={!supported || pushBusy}
                        data-testid="push-toggle-switch"
                    />
                </div>
                {rows.map((r) => (
                    <div key={r.key} className="flex items-center gap-3 rounded-2xl border border-border bg-background px-4 py-3">
                        <div className="flex-1">
                            <div className="font-semibold">{r.label}</div>
                            <div className="text-xs text-muted-foreground">{r.desc}</div>
                        </div>
                        <Switch
                            checked={!!prefs[r.key]}
                            onCheckedChange={(v) => onChange({ ...prefs, [r.key]: v })}
                            data-testid={`notif-toggle-${r.key}`}
                        />
                    </div>
                ))}
            </div>
        </BottomSheet>
    );
}

function SupportSheet({ open, onClose }) {
    return (
        <BottomSheet open={open} onClose={onClose} title="Help & support" testId="support-sheet">
            <p className="text-sm text-muted-foreground">
                Something feels off? Got a bug, a feature idea, or need help with your account? We'd love to hear from you.
            </p>
            <div className="mt-4 space-y-2">
                <a
                    href="mailto:support@flatch.in?subject=Flatch%20support"
                    data-testid="support-email"
                    className="flex items-center gap-3 rounded-2xl border border-border bg-background px-4 py-4 lift"
                >
                    <div className="h-10 w-10 rounded-xl bg-accent text-accent-foreground grid place-items-center">
                        <Mail className="h-5 w-5" strokeWidth={2.5} />
                    </div>
                    <div>
                        <div className="font-semibold">Email us</div>
                        <div className="text-xs text-muted-foreground">support@flatch.in</div>
                    </div>
                </a>
                <a
                    href="https://twitter.com/flatch"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="support-twitter"
                    className="flex items-center gap-3 rounded-2xl border border-border bg-background px-4 py-4 lift"
                >
                    <div className="h-10 w-10 rounded-xl bg-accent text-accent-foreground grid place-items-center">
                        <MessageSquare className="h-5 w-5" strokeWidth={2.5} />
                    </div>
                    <div>
                        <div className="font-semibold">DM us on Twitter</div>
                        <div className="text-xs text-muted-foreground">@flatch</div>
                    </div>
                </a>
            </div>
            <div className="mt-4 rounded-2xl border border-border bg-background p-4 text-xs text-muted-foreground">
                <div className="font-bold text-foreground mb-1">FAQ</div>
                <p><strong>Can I cancel Premium?</strong> Yes, anytime — no lock-in.</p>
                <p className="mt-1"><strong>How do matches work?</strong> Mutual likes create a match. Then you can chat.</p>
                <p className="mt-1"><strong>Is my info shared?</strong> Only what's on your profile — never your contact info.</p>
            </div>
        </BottomSheet>
    );
}

function ReferralSheet({ open, onClose, user }) {
    const [code, setCode] = useState(null);
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        if (!open) return;
        api.get("/referral/link").then(({ data }) => setCode(data.code)).catch(() => {});
    }, [open]);
    const link = code ? `${window.location.origin}/?ref=${code}` : "";
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
            toast.success("Link copied");
        } catch (_) {
            toast.error("Couldn't copy");
        }
    };
    const share = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: "Join me on Flatch",
                    text: `I'm on Flatch — a GenZ roommate finder. Come vibe:`,
                    url: link,
                });
            } catch (_) {}
        } else copy();
    };
    return (
        <BottomSheet open={open} onClose={onClose} title="Invite a friend" testId="referral-sheet">
            <p className="text-sm text-muted-foreground">
                Send your link. When they sign up, you both get warm vibes ✨ (and priority in each other's feeds).
            </p>
            <div className="mt-4 rounded-3xl border border-border bg-background p-4 flex items-center gap-2">
                <div className="flex-1 min-w-0 font-mono text-xs truncate" data-testid="referral-link">
                    {link || "Loading…"}
                </div>
                <button
                    onClick={copy}
                    data-testid="referral-copy-btn"
                    className="h-10 w-10 rounded-xl bg-accent text-accent-foreground grid place-items-center lift"
                >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" strokeWidth={2.5} />}
                </button>
            </div>
            <Button onClick={share} data-testid="referral-share-btn" className="mt-4 h-12 w-full rounded-2xl font-semibold lift">
                <Share2 className="mr-2 h-4 w-4" /> Share
            </Button>
        </BottomSheet>
    );
}
